// 3rd party imports
import * as React from 'react';

// bernie
import { ContextStore, RouteStore } from 'bernie-core';

// shared-ui
import { ILocalization, LocalizedComponentProps } from '@shared-ui/localization-context';

// mobx
import { Program, UserInfo } from 'models';

// stores
import { UserAuthStore } from 'stores';

// ui
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkSpacing } from 'uitk-react-spacing';

// components
import { ProgramCardCTA } from './program-card-cta';
import { ProgramCardFeatures } from './program-card-features';
import { ProgramCardHeader } from './program-card-header';
import { ProgramCardLobs } from './program-card-lobs';
import { ProgramCardLogo } from './program-card-logo';
import { ProgramCardUser } from './program-card-user';
import { VerifyPartnerDialog } from '../verify-partner-dialog';
import { JoinAffiliateNetworkDialog } from '../join-affiliate-network-dialog';
import { CommercialRulesDialog } from '../commercial-rules-dialog';

export interface ProgramCardProps extends LocalizedComponentProps {
  context?: ContextStore;
  l10n?: ILocalization;
  program: Program;
  partnerId: string;
  camref?: string;
  programStatus: string;
  routeStore: RouteStore;
  user: UserInfo;
  userAuth: UserAuthStore;
}

export const ProgramCard: React.FunctionComponent<ProgramCardProps> = ({
  context,
  l10n,
  program,
  partnerId,
  camref,
  programStatus,
  routeStore,
  user,
  userAuth,
}) => {
  const partnerSignedUp = partnerId && programStatus && programStatus !== 'UNKNOWN';
  const isProgramApproved = partnerId && programStatus === 'APPROVED';
  const [programStatusVerified, setProgramStatusVerified] = React.useState(false);

  function updateProgramStatusVerified() {
    setProgramStatusVerified(true);
  }

  return (
    <UitkCard border={true} padded={true}>
      <UitkCardContentSection>
        <ProgramCardLogo program={program} />
      </UitkCardContentSection>

      <UitkCardContentSection>
        <UitkSpacing padding={{ blockstart: 'six', inline: 'four' }}>
          <ProgramCardHeader l10n={l10n} program={program} />
        </UitkSpacing>
      </UitkCardContentSection>

      {program.features && (
        <UitkCardContentSection>
          <UitkSpacing padding={{ blockstart: 'six', inline: 'four' }}>
            <ProgramCardFeatures l10n={l10n} program={program} />
          </UitkSpacing>
        </UitkCardContentSection>
      )}

      {program.lobs && (
        <UitkCardContentSection>
          <UitkSpacing padding={{ blockstart: 'six', inline: 'four' }}>
            <ProgramCardLobs l10n={l10n} program={program} />
          </UitkSpacing>
        </UitkCardContentSection>
      )}

      <UitkCardContentSection>
        <UitkSpacing padding={{ blockstart: 'six', inline: 'four' }}>
          <ProgramCardCTA
            context={context}
            l10n={l10n}
            program={program}
            isProgramApproved={isProgramApproved}
            programStatus={programStatus}
          />
        </UitkSpacing>
      </UitkCardContentSection>

      <UitkCardContentSection>
        <UitkSpacing padding={{ blockstart: 'six', inline: 'four' }}>
          <ProgramCardUser
            l10n={l10n}
            partnerId={partnerId}
            program={program}
            partnerSignedUp={partnerSignedUp}
            programStatus={programStatus}
            camref={camref}
            programStatusVerified={programStatusVerified}
          />
        </UitkSpacing>
      </UitkCardContentSection>

      <VerifyPartnerDialog
        dialogId={`verifyPartner-${program.name}`}
        l10n={l10n}
        program={program}
        routeStore={routeStore}
        user={user}
        userAuth={userAuth}
        updateProgramStatusVerified={updateProgramStatusVerified}
      />
      <JoinAffiliateNetworkDialog
        dialogId={`joinAffiliateNetwork-${program.name}`}
        l10n={l10n}
        routeStore={routeStore}
        locale={context.locale}
        isPreviewMode={false}
        showManageProgramLink={false}
        program={program}
      />
      <CommercialRulesDialog
        dialogId={`commercialRules-${program.name}`}
        l10n={l10n}
        routeStore={routeStore}
        programName={program.name}
      />
    </UitkCard>
  );
};
